import {
  $convertFromMarkdownString,
  type TextMatchTransformer,
  TRANSFORMERS,
} from "@lexical/markdown";
import { $restoreEditorState } from "@lexical/utils";
import { $getNodeByKey, type LexicalEditor } from "lexical";
import { isEmpty } from "lodash-es";
import { getAspectRatio } from "../../../../utils/aspect-ratio";
import { $createCustomEmojiNode } from "../nodes/custom-emoji/custom-emoji-node";
import { $createDesignHuddleNodeFromAsset } from "../nodes/design-huddle-mui";
import { $isImageNode } from "../nodes/image-node";
import { $createImageNode } from "../nodes/image-node-mui";
import type { SerialisedEditorDesign } from "../types/serialised-editor-design";

const isMarkdown = (
  design: SerialisedEditorDesign | undefined,
): design is { type: "markdown"; markdown: string } =>
  design !== undefined && "type" in design && design?.type === "markdown";

const HUDDLE = (format: "email" | "chat" | "sms"): TextMatchTransformer => ({
  importRegExp:
    /!(?:\[[^[]*)\]\((https:\/\/images[^.]+\.changeengine.com\/([^/]+)\/[^/]+\/[^/]+\/[^/]+\?p=([^)]+))\)/,
  regExp: /nope/,
  replace: (textNode, match) => {
    const [, preview, account_id, project_id] = match;
    const imageNode = $createDesignHuddleNodeFromAsset(
      false,
      account_id!,
      project_id,
      1,
      1,
      undefined,
      preview,
    );
    if (format === "chat") (textNode.getParent() ?? textNode).replace(imageNode);
    else textNode.replace(imageNode);
  },
  type: "text-match",
  dependencies: [],
});

const imageTransformer = (
  format: "email" | "chat" | "sms",
  editor: LexicalEditor,
): TextMatchTransformer => ({
  importRegExp: /!(?:\[([^[]*)\])(?:\(([^(]+)\))/,
  regExp: /nope/,
  replace: (textNode, match) => {
    const [, , src] = match;
    const imageNode = $createImageNode(false, src!, 1, 1);
    if (format === "chat") (textNode.getParent() ?? textNode).replace(imageNode);
    else textNode.replace(imageNode);

    const nodeKey = imageNode.getKey();
    void getAspectRatio(src!)
      .then((aspect) => {
        editor.update(() => {
          const node = $getNodeByKey(nodeKey);
          if ($isImageNode(node) && node.__url === src) {
            node.setSize(node.__proportionOfMaxWidth, aspect);
          }
        });
      })
      .catch(() => undefined);
  },
  type: "text-match",
  dependencies: [],
});

const EMOJI: TextMatchTransformer = {
  importRegExp: /:([a-zA-Z0-9_+-]+):/,
  regExp: /nope/,
  replace: (textNode, match) => {
    const [, shortcode] = match;
    const emojiNode = $createCustomEmojiNode(shortcode!);
    textNode.replace(emojiNode);
  },
  type: "text-match",
  dependencies: [],
};

export const $loadDesign = async (
  design: SerialisedEditorDesign | undefined,
  format: "email" | "chat" | "sms",
  // eslint-disable-next-line @typescript-eslint/require-await
): Promise<((e: LexicalEditor) => void) | undefined> =>
  isEmpty(design)
    ? undefined
    : isMarkdown(design)
      ? (e) =>
          $convertFromMarkdownString(design.markdown, [
            HUDDLE(format),
            imageTransformer(format, e),
            EMOJI,
            ...TRANSFORMERS,
          ])
      : (e) => $restoreEditorState(e, e.parseEditorState(design));
